import { Button } from "@/components/ui/button";
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/utils";
import analytics from "@/utils/analytics";
import { Skill } from "@cohesive/api-client";
import { Icon, INSERT_SKILL_COMMAND } from "@cohesive/ui-components";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { ScrollText, X } from "lucide-react";

interface SkillsMenuProps {
  iconClassName?: string;
  triggerButtonClassName?: string;
  skillsEnabled: boolean;
  filteredSkills: Skill[];
  skillsFilter: string;
  setSkillsFilter: (filter: string) => void;
  skillsMenuOpen: boolean;
  setSkillsMenuOpen: (open: boolean) => void;
  setMentionMode: (mode: "@" | "/" | null) => void;
  focusTextarea: () => void;
  leftBoundary: number;
  containerRect?: DOMRect | null;
}

export const SkillsMenu: React.FC<SkillsMenuProps> = ({
  iconClassName,
  triggerButtonClassName,
  skillsEnabled,
  filteredSkills,
  skillsFilter,
  setSkillsFilter,
  skillsMenuOpen,
  setSkillsMenuOpen,
  setMentionMode,
  focusTextarea,
  leftBoundary,
}) => {
  const [editor] = useLexicalComposerContext();

  return (
    <div className="z-9 order-2">
      <Popover
        open={skillsMenuOpen}
        onOpenChange={(open) => {
          setSkillsMenuOpen(open);
          if (!open) {
            focusTextarea();
          }
        }}
      >
        <Tooltip delayDuration={0}>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <Button
                variant="ghost"
                size="icon-sm"
                className={cn(
                  "text-muted-foreground hover:text-foreground transition-colors",
                  triggerButtonClassName,
                )}
              >
                <ScrollText className={iconClassName} />
              </Button>
            </PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent side="bottom">Add skill '/'</TooltipContent>
        </Tooltip>
        <PopoverContent
          align="start"
          sideOffset={8}
          collisionPadding={{ left: leftBoundary + 20 }}
          className="w-[384px] min-w-[384px] p-2 rounded-[14px] border transition-all duration-300"
          onCloseAutoFocus={(e) => {
            // Prevent popover from auto-focusing the trigger button on close.
            // This ensures focus returns to the textarea input instead.
            e.preventDefault();
          }}
        >
          <Command
            className="bg-transparent rounded-none overflow-hidden w-full"
            shouldFilter={false}
          >
            <CommandList className="max-h-[520px] overflow-y-auto scrollbar-thin scrollbar-thumb-neutral-300 dark:scrollbar-thumb-neutral-600 scrollbar-track-transparent">
              <CommandGroup className="[&_.border-b]:border-0">
                <div className="relative">
                  <CommandInput
                    placeholder="Search Skills..."
                    value={skillsFilter}
                    onValueChange={setSkillsFilter}
                    className="h-11 pr-8"
                  />
                  {skillsFilter && (
                    <button
                      type="button"
                      aria-label="Clear skills search"
                      onClick={() => setSkillsFilter("")}
                      className="absolute right-5 top-1/2 -translate-y-1/2 p-1"
                    >
                      <X className="w-4 h-4 text-muted-foreground hover:text-foreground transition-colors" />
                    </button>
                  )}
                </div>

                {filteredSkills.length > 0 ? (
                  <>
                    {skillsEnabled &&
                      filteredSkills.map((skill) => (
                        <CommandItem
                          key={`skill-${skill.id}`}
                          className="flex items-center gap-3 h-11 px-4"
                          onSelect={() => {
                            editor.dispatchCommand(INSERT_SKILL_COMMAND, {
                              id: skill.id,
                              name: skill.name,
                            });
                            analytics.trackSkillSelected({ skill });
                            setSkillsMenuOpen(false);
                            setMentionMode(null);
                            setTimeout(() => focusTextarea(), 0);
                          }}
                        >
                          <div className="w-5 h-5 shrink-0 flex items-center justify-center">
                            {skill.icon && skill.icon.type ? (
                              <Icon icon={skill.icon} size={20} />
                            ) : (
                              <ScrollText className="w-5 h-5" />
                            )}
                          </div>
                          <span>{skill.name}</span>
                        </CommandItem>
                      ))}
                  </>
                ) : null}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
};
